import { useEffect, useState } from "react";
import ViewOrderTable from "./ViewOrderTable";
import { OrderItem } from "../../Models/OrderItem";
import OrderApi from "../../Api/OrderApi";
import { Order } from "../../Models/Order";
import Loader from "../Common/Loader";
import { toast } from "react-toastify";
import { formatNumbers } from "../../Utils";
import { Button } from "flowbite-react";
import { Equipment } from "../../Models/Equipment";
import { useAppSelector } from "../../Reducers/store";
import { SavedItem } from "../../Models/SavedItem";
import SavedItemsTable from "./SavedItemsTable";
import { Link } from "react-router-dom";

export default function ViewOrder() {
  const { userModel } = useAppSelector((state) => state.user);
  const [loading, setIsLoading] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<Order | null>(null);
  const [savedData, setSavedData] = useState<SavedItem[] | null>(null);

  const getOrder = () => {
    setIsLoading(true);
    const openOrderId =
      localStorage.getItem("openOrderId") &&
      Number(localStorage.getItem("openOrderId")) >= 0
        ? Number(localStorage.getItem("openOrderId"))
        : 0;
    OrderApi.getOrder(openOrderId)
      .then((res) => {
        if (res.data?.id > 0) {
          setOrderData(res.data);
          localStorage.setItem("openOrderId", String(res.data.id));

          // Use the ACTUAL order id for the count (fixes earlier 0 bug)
          OrderApi.getOrderItemsCount(res.data.id)
            .then((countRes) => {
              if (countRes.data !== null && countRes.data !== undefined) {
                localStorage.setItem("orderItemsCount", String(countRes.data));
                window.dispatchEvent(new Event("updateOrderItemCount"));
              }
            })
            .catch(() => {
              /* ignore, keep UI responsive */
            })
            .finally(() => setIsLoading(false));
        } else {
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  };

  const getSavedItems = () => {
    if (!userModel) return;
    setIsLoading(true);
    OrderApi.getSavedItems(userModel.id)
      .then((res) => {
        setSavedData(res.data ?? []);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getOrder();
    getSavedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function removeFromCart(id: number) {
    const shouldRemove = window.confirm(
      "Are you sure you want to remove this from your cart?"
    );
    if (!shouldRemove) return;

    setIsLoading(true);
    OrderApi.deleteOrderItem(id)
      .then((res) => {
        if (res.data?.success) {
          toast.success("Item removed");
          getOrder();
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  }

  function removeFromSaved(id: number) {
    const shouldRemove = window.confirm(
      "Are you sure you want to remove this from your saved items?"
    );
    if (!shouldRemove) return;

    setIsLoading(true);
    OrderApi.deleteSavedItem(id)
      .then((res) => {
        if (res.data?.success) {
          toast.success("Item removed");
          getSavedItems();
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  }

  function moveToCart(item: SavedItem) {
    const stored = localStorage.getItem("openOrderId");
    const openOrderId = stored && Number(stored) >= 0 ? Number(stored) : 0;
    if (openOrderId <= 0) return;

    setIsLoading(true);
    OrderApi.moveSavedItemToCart(item, openOrderId)
      .then((res) => {
        if (res.data?.success) {
          toast.success("Item saved");
          getOrder();
          getSavedItems();
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  }

  function saveForLater(equipment: Equipment) {
    setIsLoading(true);
    OrderApi.saveItem(equipment)
      .then((res) => {
        if (res.data?.success) {
          toast.success("Item saved");
          getOrder();
          getSavedItems();
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setIsLoading(false));
  }

  function updateItemQuantity(item: OrderItem, quantity: number) {
    const updated = { ...item, quantity };
    OrderApi.updateOrderItemQuantity(updated)
      .then((res) => {
        if (res.data?.success) {
          getOrder();
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  // Compute total defensively
  const total =
    orderData?.orderItems?.reduce((acc, item) => {
      const equipment = item.equipment;
      const isTrade = equipment?.oemvendor?.startsWith("1");
      const price = isTrade
        ? equipment?.tradePrice ?? 0
        : equipment?.special1Price ?? 0;
      return acc + price * (item.quantity ?? 0);
    }, 0) ?? 0;

  // ViewOrder.tsx (only the layout bits changed)
  return (
    <div className="w-full">
      <div className="container mx-auto p-2">
        {/* Header */}
        <div className="mb-2 border-b-2 flex justify-between items-start gap-3">
          <div className="flex flex-col">
            <h1 className="text-gray-900 font-bold text-4xl">Cart:</h1>
            {orderData?.client?.accountNumber && (
              <div className="text-gray-600 text-lg mt-1">
                Account Number:{" "}
                <span className="font-semibold">
                  {orderData.client.accountNumber}
                </span>
              </div>
            )}
          </div>
          {orderData && (
            <div className="flex gap-3 flex-wrap items-end">
              <div className="flex gap-3 font-bold text-2xl items-end">
                <div>Total:</div>
                <div>${formatNumbers(total)}</div>
              </div>
              <div>
                <Link to={"/orders/checkout"}>
                  <Button type="button" className="buttonMain">
                    Checkout
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Table area — no height/overflow here */}
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div className="bg-white/60 backdrop-blur-sm rounded p-4">
                <Loader />
              </div>
            </div>
          )}

          {orderData ? (
            <ViewOrderTable
              data={orderData.orderItems ?? []}
              removeFromCart={removeFromCart}
              saveForLater={saveForLater}
              updateItemQuantity={updateItemQuantity}
            />
          ) : (
            <div>Your order is empty</div>
          )}

          {savedData && savedData.length > 0 && (
            <div className="pt-10 border-t-2">
              <div className="mb-2 flex justify-between">
                <h1 className="text-gray-900 font-bold text-4xl">
                  Saved Items:
                </h1>
              </div>
              {/* Make sure SavedItemsTable also paginates and does NOT add overflow-y-auto */}
              <SavedItemsTable
                data={savedData ?? []}
                removeFromSaved={removeFromSaved}
                moveToCart={moveToCart}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
